import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import PublishIcon from '@material-ui/icons/Publish'

import { useOrganizationCompanyList } from '../../hooks/useOrganizationCompanyList'
import { useUser } from '../../hooks/useUser'
import {
    ApplicationWorkbookBuilderTab,
    useApplyOrgWorkbookToProperties,
    useApplicationWorkbookTemplates,
    useCreateOrgApplicationWorkbook,
    useOrgApplicationWorkbooks,
} from '../ApplicationWorkbook'
import {
    WorkbookConfigCreateFields,
    WorkbookConfigStartMode,
} from '../ApplicationWorkbook/WorkbookConfigCreateFields'
import { formatOrgWorkbookProvenance } from '../ApplicationWorkbook/workbookDisplay'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        width: '100%',
    },
    headerRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
    },
    configSelect: {
        minWidth: 260,
    },
    loading: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(4),
    },
    buttonRow: {
        display: 'flex',
        gap: theme.spacing(1),
    },
    propertyList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(1),
        maxHeight: 280,
        overflowY: 'auto',
    },
    applyActions: {
        display: 'flex',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
}))

const DEFAULT_START_MODE: WorkbookConfigStartMode = 'template'

function resetCreateDialogState(
    setNewWorkbookName: (value: string) => void,
    setStartMode: (value: WorkbookConfigStartMode) => void,
    setTemplateKey: (value: string) => void,
) {
    setNewWorkbookName('')
    setStartMode(DEFAULT_START_MODE)
    setTemplateKey('')
}

export const EzFolioApplicationWorkbookTab: React.FC = () => {
    const classes = useStyles()
    const { organization } = useUser()
    const organizationId = organization

    const apartmentsQuery = useOrganizationCompanyList(
        organizationId,
        'APARTMENT',
    )
    const apartments = apartmentsQuery.data ?? []

    const workbooksQuery = useOrgApplicationWorkbooks(organizationId)
    const templatesQuery = useApplicationWorkbookTemplates()
    const createMutation = useCreateOrgApplicationWorkbook(organizationId)
    const applyMutation = useApplyOrgWorkbookToProperties(organizationId)

    const [selectedWorkbookId, setSelectedWorkbookId] = useState<number | ''>(
        '',
    )
    const [createOpen, setCreateOpen] = useState(false)
    const [applyOpen, setApplyOpen] = useState(false)
    const [newWorkbookName, setNewWorkbookName] = useState('')
    const [startMode, setStartMode] = useState<WorkbookConfigStartMode>(
        DEFAULT_START_MODE,
    )
    const [templateKey, setTemplateKey] = useState('')
    const [selectedPropertyIds, setSelectedPropertyIds] = useState<number[]>([])

    const workbooks = workbooksQuery.data ?? []
    const templates = templatesQuery.data ?? []

    const templatesByKey = useMemo(
        () => new Map(templates.map((template) => [template.key, template])),
        [templates],
    )

    useEffect(() => {
        if (workbooks.length === 0) {
            setSelectedWorkbookId('')
            return
        }
        if (
            selectedWorkbookId === '' ||
            !workbooks.some((w) => w.id === selectedWorkbookId)
        ) {
            setSelectedWorkbookId(workbooks[0].id)
        }
    }, [workbooks, selectedWorkbookId])

    useEffect(() => {
        if (applyOpen) {
            setSelectedPropertyIds(apartments.map((apt) => apt.id))
        }
    }, [applyOpen, apartments])

    const closeCreateDialog = () => {
        setCreateOpen(false)
        resetCreateDialogState(setNewWorkbookName, setStartMode, setTemplateKey)
    }

    const formatWorkbookLabel = (workbook: typeof workbooks[number]) => {
        const provenance = formatOrgWorkbookProvenance(workbook, templatesByKey)
        const suffix = provenance ? ` — ${provenance}` : ''
        const locked = !workbook.is_editable ? ' (read-only)' : ''
        return `${workbook.name}${suffix}${locked}`
    }

    const handleCreate = () => {
        const name = newWorkbookName.trim()
        if (!name) return
        if (startMode === 'template' && !templateKey) return

        const payload =
            startMode === 'template'
                ? { name, template_key: templateKey }
                : { name }

        createMutation.mutate(payload, {
            onSuccess: (data) => {
                closeCreateDialog()
                setSelectedWorkbookId(data.id)
            },
        })
    }

    const toggleProperty = (propertyId: number) => {
        setSelectedPropertyIds((prev) =>
            prev.includes(propertyId)
                ? prev.filter((id) => id !== propertyId)
                : [...prev, propertyId],
        )
    }

    const handleApply = () => {
        if (selectedWorkbookId === '' || selectedPropertyIds.length === 0)
            return
        applyMutation.mutate(
            {
                workbookId: selectedWorkbookId as number,
                property_ids: selectedPropertyIds,
            },
            { onSuccess: () => setApplyOpen(false) },
        )
    }

    if (organizationId == null) {
        return (
            <Typography color="textSecondary">
                Organization context is required to manage application
                workbooks.
            </Typography>
        )
    }

    if (workbooksQuery.isLoading) {
        return (
            <Box className={classes.loading}>
                <CircularProgress size={28} />
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.headerRow}>
                <FormControl
                    variant="outlined"
                    size="small"
                    className={classes.configSelect}
                >
                    <InputLabel>Org workbook</InputLabel>
                    <Select
                        value={selectedWorkbookId}
                        onChange={(e) =>
                            setSelectedWorkbookId(e.target.value as number)
                        }
                        label="Org workbook"
                    >
                        {workbooks.map((workbook) => (
                            <MenuItem key={workbook.id} value={workbook.id}>
                                {formatWorkbookLabel(workbook)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box className={classes.buttonRow}>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateOpen(true)}
                    >
                        New workbook
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PublishIcon />}
                        disabled={
                            selectedWorkbookId === '' || apartments.length === 0
                        }
                        onClick={() => setApplyOpen(true)}
                    >
                        Apply to properties
                    </Button>
                </Box>
            </Box>

            <ApplicationWorkbookBuilderTab
                scope="org"
                organizationId={organizationId}
                workbookId={
                    selectedWorkbookId === '' ? null : selectedWorkbookId
                }
            />

            <Dialog
                open={createOpen}
                onClose={closeCreateDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Create org workbook</DialogTitle>
                <DialogContent>
                    <WorkbookConfigCreateFields
                        name={newWorkbookName}
                        onNameChange={setNewWorkbookName}
                        startMode={startMode}
                        onStartModeChange={setStartMode}
                        templateKey={templateKey}
                        onTemplateKeyChange={setTemplateKey}
                        templates={templates}
                        templatesLoading={templatesQuery.isLoading}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeCreateDialog}>Cancel</Button>
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={
                            !newWorkbookName.trim() ||
                            (startMode === 'template' && !templateKey) ||
                            createMutation.isLoading
                        }
                        onClick={handleCreate}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={applyOpen}
                onClose={() => setApplyOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Apply workbook to properties</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" paragraph>
                        Creates independent property copies. Org edits will not
                        update properties already applied.
                    </Typography>
                    <Box className={classes.applyActions}>
                        <Button
                            size="small"
                            onClick={() =>
                                setSelectedPropertyIds(
                                    apartments.map((apt) => apt.id),
                                )
                            }
                        >
                            Select all
                        </Button>
                        <Button
                            size="small"
                            onClick={() => setSelectedPropertyIds([])}
                        >
                            Clear all
                        </Button>
                    </Box>
                    <Box className={classes.propertyList}>
                        {apartments.map((apt) => (
                            <FormControlLabel
                                key={apt.id}
                                control={
                                    <Checkbox
                                        color="primary"
                                        checked={selectedPropertyIds.includes(
                                            apt.id,
                                        )}
                                        onChange={() => toggleProperty(apt.id)}
                                    />
                                }
                                label={apt.name}
                            />
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setApplyOpen(false)}>Cancel</Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={handleApply}
                        disabled={
                            selectedPropertyIds.length === 0 ||
                            applyMutation.isLoading
                        }
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
